import { useLayoutEffect, useRef, useState } from "react";
import { format, isToday, isTomorrow } from "date-fns";
import { Calendar3, CalendarX, ChevronDown } from "react-bootstrap-icons";
import { PuffLoader } from "react-spinners";
import { itemAnchorDate } from "@areas/calendar/common";
import ScheduleChip from "./ScheduleChip";
import ScheduleItemModal from "./ScheduleItemModal";
import { useSchedule } from "../hooks/useSchedule";

const dayLabel = (date) => {
  if (isToday(date)) return "Today";
  if (isTomorrow(date)) return "Tomorrow";
  return format(date, "EEE d MMM");
};

// Group consecutive items by their day (list is already sorted by anchor date — an event's start,
// a task's due).
const groupByDay = (events) => {
  const groups = [];
  for (const ev of events) {
    const anchor = itemAnchorDate(ev);
    const key = anchor ? format(anchor, "yyyy-MM-dd") : "undated";
    const last = groups[groups.length - 1];
    if (last?.key === key) last.items.push(ev);
    else groups.push({ key, date: anchor, items: [ev] });
  }
  return groups;
};

const SchedulePanel = () => {
  const { events, isLoading, error } = useSchedule();
  const [selectedItem, setSelectedItem] = useState(null);
  // The scroll-hint chevron shows only until the user starts scrolling; the whole bottom fade lifts
  // once there's nothing more below (scrolled to the end, or the list never overflowed).
  const [scrolled, setScrolled] = useState(false);
  const [atBottom, setAtBottom] = useState(true);
  const scrollRef = useRef(null);
  const groups = groupByDay(events);

  const trackScroll = (el) => {
    setScrolled(el.scrollTop > 0);
    setAtBottom(el.scrollHeight - el.scrollTop - el.clientHeight <= 4);
  };

  // Re-evaluate the fade when the schedule's contents change: a list that now fits shows no fade.
  useLayoutEffect(() => {
    if (scrollRef.current) trackScroll(scrollRef.current);
  }, [events.length]);

  return (
    <div className="flex shrink-0 flex-col gap-4">
      <div className="flex shrink-0 items-center gap-2">
        <Calendar3 className="size-4 text-zinc-500" />
        <h4 className="text-sm font-semibold text-zinc-800">Scheduled</h4>
      </div>

      <div className="flex min-h-0 flex-col rounded-2xl border border-zinc-200 bg-white p-3">
        {isLoading ? (
          <div className="flex justify-center py-6">
            <PuffLoader color="#F95816" size={28} />
          </div>
        ) : error ? (
          <p className="px-1 py-2 text-xs text-zinc-400">
            Couldn't load your schedule.
          </p>
        ) : events.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-6 text-zinc-400">
            <CalendarX className="size-6" />
            <p className="text-xs">Nothing scheduled</p>
          </div>
        ) : (
          <div className="relative min-h-0">
            <div
              ref={scrollRef}
              className="flex max-h-36 min-h-0 flex-col gap-3 overflow-y-auto"
              onScroll={(e) => trackScroll(e.currentTarget)}
            >
              {groups.map((group) => (
                <div key={group.key} className="flex flex-col gap-1.5">
                  <p className="px-1 text-[11px] font-semibold uppercase tracking-wide text-zinc-400">
                    {group.date ? dayLabel(group.date) : "Undated"}
                  </p>
                  {group.items.map((ev) => (
                    <ScheduleChip
                      key={ev.occurrenceKey ?? ev.uid}
                      item={ev}
                      onClick={setSelectedItem}
                    />
                  ))}
                </div>
              ))}
            </div>
            <div
              className={`pointer-events-none absolute inset-x-0 bottom-0 flex h-8 items-end justify-center bg-gradient-to-t from-white to-transparent transition-opacity ${atBottom ? "opacity-0" : "opacity-100"}`}
            >
              <ChevronDown
                aria-hidden="true"
                className={`mb-0.5 size-3.5 text-zinc-400 transition-opacity ${scrolled ? "opacity-0" : "opacity-100"}`}
              />
            </div>
          </div>
        )}
      </div>

      {selectedItem && (
        <ScheduleItemModal
          item={selectedItem}
          onClose={() => setSelectedItem(null)}
        />
      )}
    </div>
  );
};

export default SchedulePanel;
